import { useEffect } from 'react';
import { EditorContent, useEditor, type Editor as TiptapEditor } from '@tiptap/react';
import { clientEditorExtensions, editorExtensions } from './extensions';
import {
  setThreadDecorations,
  type ThreadAnchor,
} from './CommentDecorations';
import {
  BlockCommentTargets,
  BLOCK_COMMENT_EVENT,
  type BlockCommentDetail,
} from './BlockCommentTargets';

// The four lists, built once at module scope so a re-render never hands
// useEditor a fresh array. `briefControls` picks the row, `blockComments` the
// column.
const EXTENSIONS = {
  admin: editorExtensions,
  adminWithBlocks: [...editorExtensions, BlockCommentTargets],
  client: clientEditorExtensions,
  clientWithBlocks: [...clientEditorExtensions, BlockCommentTargets],
};

export interface SelectionInfo {
  from: number;
  to: number;
  empty: boolean;
  text: string;
}

interface Props {
  initialDoc: unknown;
  onUpdate: (doc: unknown) => void;
  editable?: boolean;
  onReady?: (editor: TiptapEditor) => void;
  onSelectionChange?: (info: SelectionInfo) => void;
  threads?: ThreadAnchor[];
  activeThreadId?: string | null;
  /** Enables the per-block "+" comment targets (read-only client review only). */
  blockComments?: boolean;
  /** Fired when a block "+" target is tapped/activated — the whole-block range. */
  onBlockTap?: (detail: BlockCommentDetail) => void;
  /** Build the audience chip and ⋯ menu on interview-brief fields. False on
   *  every client-facing editor: those controls are Henry's, and the server
   *  rehydrates a brief's field attrs from the master doc regardless. */
  briefControls?: boolean;
}

export function Editor({
  initialDoc,
  onUpdate,
  editable = true,
  onReady,
  onSelectionChange,
  threads,
  activeThreadId = null,
  blockComments = false,
  onBlockTap,
  briefControls = true,
}: Props) {
  const editor = useEditor({
    extensions: briefControls
      ? blockComments
        ? EXTENSIONS.adminWithBlocks
        : EXTENSIONS.admin
      : blockComments
        ? EXTENSIONS.clientWithBlocks
        : EXTENSIONS.client,
    content: initialDoc as never,
    editable,
    autofocus: editable && window.matchMedia('(min-width: 768px)').matches ? 'end' : false,
    onUpdate: ({ editor }) => onUpdate(editor.getJSON()),
    onCreate: ({ editor }) => onReady?.(editor as TiptapEditor),
    onSelectionUpdate: ({ editor }) => {
      if (!onSelectionChange) return;
      const { from, to, empty } = editor.state.selection;
      const text = empty ? '' : editor.state.doc.textBetween(from, to, ' ');
      onSelectionChange({ from, to, empty, text });
    },
  });

  // Sync threads → decoration plugin whenever they change.
  useEffect(() => {
    if (!editor || !threads) return;
    setThreadDecorations(editor.view, threads, activeThreadId);
  }, [editor, threads, activeThreadId]);

  // Bridge the block-target widget's DOM event back up to React.
  useEffect(() => {
    if (!editor || !onBlockTap) return;
    const dom = editor.view.dom;
    const handler = (e: Event) => onBlockTap((e as CustomEvent<BlockCommentDetail>).detail);
    dom.addEventListener(BLOCK_COMMENT_EVENT, handler as EventListener);
    return () => dom.removeEventListener(BLOCK_COMMENT_EVENT, handler as EventListener);
  }, [editor, onBlockTap]);

  return <EditorContent editor={editor} className="hsp-editor" />;
}

export type { TiptapEditor };
